// Informações complementares da Diretoria (spec `director/workspace-navigation`): as páginas de
// apoio, agrupadas em três seções. É também o mapa rota → seção que dá a trilha
// "Diretoria › Informações complementares › <página>" às páginas que já existiam, sem mudar os
// endereços delas.

export const DIRECTOR_BASE_PATH = "/dashboard/director";
export const COMPLEMENTARY_PATH = `${DIRECTOR_BASE_PATH}/complementary`;
export const COMPLEMENTARY_LABEL = "Informações complementares";

// As quatro telas principais, na ordem do menu, com a pergunta de cada uma.
export const PRIMARY_SCREENS = [
  {
    key: "dashboard",
    path: `${DIRECTOR_BASE_PATH}/dashboard`,
    label: "Visão geral",
    question: "As obras dão lucro? Vai faltar dinheiro?",
    capability: "director.view",
  },
  {
    key: "cashflow",
    path: `${DIRECTOR_BASE_PATH}/cashflow`,
    label: "Fluxo de caixa mensal",
    question: "Quando o caixa aperta?",
    capability: "director.cashflow.read",
  },
  {
    key: "receivables-commitments",
    path: `${DIRECTOR_BASE_PATH}/receivables-commitments`,
    label: "Recebíveis × compromissos",
    question: "Quem se paga e quem precisa de aporte?",
    capability: "director.cashflow.read",
  },
  {
    key: "delinquency",
    path: `${DIRECTOR_BASE_PATH}/delinquency`,
    label: "Inadimplência",
    question: "Quem deve, quanto e há quanto tempo?",
    capability: "receivables.details.read",
  },
];

export const primaryScreen = (key) => PRIMARY_SCREENS.find((screen) => screen.key === key);

// `capability` é a mesma da rota em `App.js`; `paths` lista os endereços que pertencem à página
// (o primeiro é o do cartão). Prefixos cobrem os detalhes (ex.: o cliente de Clientes e
// recebimentos).
export const COMPLEMENTARY_SECTIONS = [
  {
    key: "premises",
    title: "Premissas e cenários",
    pages: [
      {
        key: "premises",
        label: "Premissas e cenários",
        question: "O que o Sienge não sabe: unidades a vender, ritmo, condição, terreno, taxa e os cenários Conservador e Estresse.",
        capability: "director.view",
        paths: ["premises"],
      },
    ],
  },
  {
    key: "analyses",
    title: "Análises detalhadas",
    pages: [
      {
        key: "overview",
        label: "Visão executiva",
        question: "Nas próximas 13 semanas há pressão de caixa?",
        capability: "director.view",
        paths: ["overview"],
      },
      {
        key: "liquidity",
        label: "Liquidez e caixa",
        question: "Semana a semana, onde o caixa aperta?",
        capability: "director.cashflow.read",
        paths: ["liquidity/weekly", "liquidity/critical-periods", "liquidity/causes"],
      },
      {
        key: "capital",
        label: "Capital necessário",
        question: "Quanto aportar em cada obra, e quando?",
        capability: "director.liquidity.read",
        paths: ["capital"],
      },
      {
        key: "scenarios",
        label: "Cenários",
        question: "E se as entradas atrasarem ou os custos subirem?",
        capability: "director.scenarios.read",
        paths: ["scenarios"],
      },
      {
        key: "dre",
        label: "DRE gerencial",
        question: "A operação dá resultado mês a mês?",
        capability: "director.dre.managerial.read",
        paths: ["dre"],
      },
      {
        key: "returns",
        label: "Retorno",
        question: "Quanto o capital investido em cada obra devolve?",
        capability: "director.return.read",
        paths: ["returns", "returns/details"],
      },
      {
        key: "customer-receipts",
        label: "Clientes e recebimentos",
        question: "Quem pagou, quem deve e quanto falta receber de cada cliente?",
        capability: "receivables.summary.read",
        paths: ["customer-receipts"],
        prefix: true,
      },
    ],
  },
  {
    key: "data",
    title: "Dados e governança",
    pages: [
      {
        key: "refresh-status",
        label: "Estado da atualização",
        question: "Os dados estão em dia? Se não, por quê?",
        capability: "director.view",
        paths: ["refresh-status"],
      },
      {
        key: "entry",
        label: "Portfólio e módulos",
        question: "Quais obras e módulos a Diretoria cobre?",
        capability: "director.view",
        paths: ["entry"],
      },
      {
        key: "centers",
        label: "Centros e títulos",
        question: "Quais centros de custo e títulos entram na conta?",
        capability: "director.portfolio.read",
        paths: ["centers", "titles"],
      },
      {
        key: "governance",
        label: "Governança dos dados",
        question: "De onde vêm os números e quão completos eles estão?",
        capability: "director.governance.read",
        paths: ["governance", "governance/portfolio"],
      },
      {
        key: "cost-planning",
        label: "Planejamento empresarial",
        question: "Qual é o plano de custo das obras fora do Sienge?",
        capability: "director.cost_plan.read",
        paths: ["cost-planning"],
      },
    ],
  },
];

export const pagePath = (page) => `${DIRECTOR_BASE_PATH}/${page.paths[0]}`;

const relativePath = (pathname = "") => {
  const base = `${DIRECTOR_BASE_PATH}/`;
  if (!pathname.startsWith(base)) return null;
  return pathname.slice(base.length).replace(/\/+$/, "");
};

// Página de Informações complementares a que o endereço pertence (com a seção), ou null.
export const complementaryPageFor = (pathname) => {
  const relative = relativePath(pathname);
  if (!relative) return null;
  for (const section of COMPLEMENTARY_SECTIONS) {
    for (const page of section.pages) {
      const match = page.paths.some((path) => relative === path || (page.prefix && relative.startsWith(`${path}/`)));
      if (match) return { section, page };
    }
  }
  return null;
};

export const isComplementaryPath = (pathname = "") => (
  pathname === COMPLEMENTARY_PATH || complementaryPageFor(pathname) !== null
);

// Seções com só as páginas que o perfil pode abrir; seção vazia sai.
export const visibleComplementarySections = (capabilities = []) => COMPLEMENTARY_SECTIONS
  .map((section) => ({
    ...section,
    pages: section.pages.filter((page) => capabilities.includes(page.capability)),
  }))
  .filter((section) => section.pages.length > 0);
